import React, { useEffect, useState } from 'react';
import useAxios from '../utils/useAxios';
import Swal from 'sweetalert2';
import { FaEye, FaInfoCircle } from 'react-icons/fa';


const styles = {
  container: {
    padding: '20px',
    minHeight: 'calc(100vh - 100px)',
    backgroundColor: '#f4f6f9',
    fontFamily: 'Segoe UI, sans-serif',
  },
  header: {
    color: '#1B019B',
    fontWeight: '600',
    fontSize: '1.5rem',
  },
  hr: {
    width: '80px',
    border: '2px solid #1B019B',
    opacity: '1',
    margin: '10px auto',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  tableHeader: {
    backgroundColor: '#1B019B',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  submitButton: {
    backgroundColor: '#1B019B',
    color: 'white',
  },
  paginationButton: {
    backgroundColor: '#fff',
    border: '1px solid #1B019B',
    color: '#1B019B',
    margin: '0 2px',
    borderRadius: '5px',
    padding: '5px 10px',
    cursor: 'pointer',
  },
  paginationActive: {
    backgroundColor: '#1B019B',
    color: '#fff',
  },
  paginationDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  detailButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#1B019B',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '10px',
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '10px',
  },
  detailItem: {
    marginBottom: '10px',
    fontSize: '14px',
  },
  justificationText: {
    backgroundColor: '#f8f9fa',
    padding: '10px',
    borderRadius: '5px',
    marginTop: '5px',
    fontSize: '14px',
  },
  fileLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    color: '#1B019B',
    textDecoration: 'none',
    marginTop: '5px',
  },
};

const AbsencesEmployePage = () => {
  const axiosInstance = useAxios();
  const [absences, setAbsences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);

  const [absenceUnJour, setAbsenceUnJour] = useState(false);
  const [dateUnique, setDateUnique] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [motif, setMotif] = useState('');
  const [justificatif, setJustificatif] = useState(null);

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ totalPages: 1, count: 0 });
  const [page, setPage] = useState(1);

  const fetchAbsences = async (pageNum = 1) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/pointage/absences/employe/?page=${pageNum}`);
      setAbsences(response.data.results);
      setPagination({
        totalPages: Math.ceil(response.data.count / 10),
        count: response.data.count,
      });
      setPage(pageNum);
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Erreur lors du chargement des absences.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAbsences();
  }, []);

  const handlePageChange = (pageNum) => {
    if (pageNum < 1 || pageNum > pagination.totalPages || loading) return;
    fetchAbsences(pageNum);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    if (absenceUnJour) {
      formData.append('date_debut', dateUnique);
      formData.append('date_fin', dateUnique);
    } else {
      formData.append('date_debut', dateDebut);
      formData.append('date_fin', dateFin);
    }
    formData.append('motif', motif);
    if (justificatif) formData.append('justificatif', justificatif);

    try {
      setLoading(true);
      await axiosInstance.post('/pointage/absences/demande/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setModalOpen(false);
      setDateUnique('');
      setDateDebut('');
      setDateFin('');
      setMotif('');
      setJustificatif(null);
      setAbsenceUnJour(false);

      Swal.fire({
        icon: 'success',
        title: 'Demande envoyée !',
        text: 'Votre demande a été soumise.',
        timer: 2000,
        showConfirmButton: false,
      });
      fetchAbsences(page);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.detail || 'Échec de l\'envoi.',

      });
    } finally {
      setLoading(false);
    }
  };

  const renderBadge = (statut) => {
    switch (statut) {
      case 'en_attente':
        return <span className="badge bg-warning text-dark">En attente</span>;
      case 'approuvée':
        return <span className="badge bg-success">Acceptée</span>;
      case 'refusée':
        return <span className="badge bg-danger">Refusée</span>;
      default:
        return <span className="badge bg-secondary">Inconnu</span>;
    }
  };

  const openDetailModal = (absence) => {
    setSelectedAbsence(absence);
    setDetailModalOpen(true);
  };

  return (
    <div>
      <div style={styles.container}>
        <div className="text-center mb-4">
          <h3 style={styles.header}>Mes demandes d'absence</h3>
          <hr style={styles.hr} />
        </div>

        <div className="d-flex justify-content-end mb-3">
          <button
            className="btn"
            style={{ backgroundColor: '#1B019B', color: 'white' }}
            onClick={() => setModalOpen(true)}
            disabled={loading}
          >
            Faire une demande
          </button>
        </div>

        <div className="card shadow-sm">
          <div className="card-body p-0" style={styles.tableContainer}>
            {loading ? (
              <p className="text-center my-4">Chargement des absences...</p>
            ) : (
              <table className="table table-hover text-center align-middle m-0">
                <thead style={styles.tableHeader}>
                  <tr>
                    <th>Date début</th>
                    <th>Date fin</th>
                    <th>Justificatif</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {absences.length > 0 ? (
                    absences.map((a) => (
                      <tr key={a.id}>
                        <td>{new Date(a.date_debut).toLocaleDateString()}</td>
                        <td>{new Date(a.date_fin).toLocaleDateString()}</td>
                        <td>
                          <button
                            onClick={() => openDetailModal(a)}
                            style={styles.detailButton}
                            title="Voir les détails"
                          >
                            <FaInfoCircle /> Détail
                          </button>
                        </td>
                        <td>{renderBadge(a.statut)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-muted">
                        Aucune demande d'absence.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Modal Détail */}
        {detailModalOpen && selectedAbsence && (
          <div style={styles.modalBackdrop} onClick={() => setDetailModalOpen(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h5 className="mb-3 text-center" style={{ color: '#1B019B' }}>
                Détail de l'absence
              </h5>

              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <strong>Date début:</strong> {new Date(selectedAbsence.date_debut).toLocaleDateString()}
                </div>
                <div style={styles.detailItem}>
                  <strong>Date fin:</strong> {new Date(selectedAbsence.date_fin).toLocaleDateString()}
                </div>
                <div style={styles.detailItem}>
                  <strong>Statut:</strong> {renderBadge(selectedAbsence.statut)}
                </div>
                <div style={styles.detailItem}>
                  <strong>Motif:</strong>
                  <div style={styles.justificationText}>{selectedAbsence.motif}</div>
                </div>
                {selectedAbsence.justificatif && (
                  <div style={styles.detailItem}>
                    <strong>Fichier joint:</strong>
                    <a
                      href={selectedAbsence.justificatif}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.fileLink}
                    >
                      <FaEye /> Voir le fichier
                    </a>
                  </div>
                )}
                {selectedAbsence.statut === 'refusée' && selectedAbsence.motif_refus && (
                  <div style={styles.detailItem}>
                    <strong>Motif de refus:</strong>
                    <div style={{ ...styles.justificationText, color: '#dc3545' }}>
                      {selectedAbsence.motif_refus}
                    </div>
                  </div>
                )}
              </div>

              <div className="text-end mt-3">
                <button className="btn btn-secondary" onClick={() => setDetailModalOpen(false)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Nouvelle Demande */}
        {modalOpen && (
          <div style={styles.modalBackdrop} onClick={() => setModalOpen(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h5 className="mb-4 text-center fw-bold" style={{ color: '#1B019B' }}>
                Nouvelle demande d'absence
              </h5>

              <form onSubmit={handleSubmit}>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="absenceUnJour"
                    checked={absenceUnJour}
                    onChange={() => setAbsenceUnJour(!absenceUnJour)}
                    disabled={loading}
                  />
                  <label className="form-check-label" htmlFor="absenceUnJour">
                    Absence sur un seul jour
                  </label>
                </div>

                {absenceUnJour ? (
                  <div className="mb-3">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateUnique}
                      onChange={(e) => setDateUnique(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Date de début</label>
                      <input
                        type="date"
                        className="form-control"
                        value={dateDebut}
                        onChange={(e) => setDateDebut(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Date de fin</label>
                      <input
                        type="date"
                        className="form-control"
                        value={dateFin}
                        onChange={(e) => setDateFin(e.target.value)}
                        required
                        min={dateDebut}
                        disabled={loading}
                      />
                    </div>
                  </>
                )}

                <div className="mb-3">
                  <label className="form-label">Motif</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={motif}
                    onChange={(e) => setMotif(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Justificatif (facultatif)</label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setJustificatif(e.target.files[0])}
                    disabled={loading}
                  />
                </div>

                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={() => setModalOpen(false)}
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={styles.submitButton}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Envoi...
                      </>
                    ) : (
                      'Envoyer'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default AbsencesEmployePage;
